/**
 * useOrgCleanersForReassign — dropdown candidates for the Assign /
 * Reassign Task affordance.
 *
 * Reads from public.profile_roles (the canonical multi-role spine
 * table shipped 2026-05-18 in migration 20260518000003) filtered to
 * role IN ('cleaner','supervisor') for the task's organization_id.
 * In small-shop tenants like Above and Beyond Clean the supervisor
 * also covers cleaning jobs, and the reassign_cleaning_task RPC was
 * widened in lockstep (migration 20260518000010) to accept either
 * grant as the new assignee.
 *
 * The RPC checks profile_roles server-side, so this hook ensures the
 * UI and the authorization layer agree on the candidate set.
 *
 * orgId is passed in (not pulled from auth) so a super_admin can
 * reassign across organizations.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'

export interface ReassignCleanerCandidate {
  user_id:   string
  full_name: string
  email:     string
}

const FIVE_MIN = 5 * 60_000

export function useOrgCleanersForReassign(orgId: string | null | undefined): UseQueryResult<ReassignCleanerCandidate[]> {
  const supabase = useSupabase()
  return useQuery({
    queryKey:  ['org-cleaners-for-reassign', orgId ?? null],
    enabled:   !!orgId,
    staleTime: FIVE_MIN,
    queryFn: async (): Promise<ReassignCleanerCandidate[]> => {
      if (!orgId) return []
      // Two-query approach instead of profiles!inner(...) embed:
      // profile_roles has TWO FKs to profiles (user_id and
      // granted_by_user_id), so PostgREST's auto-FK-detection is
      // ambiguous and may join on the wrong column, dropping every
      // row whose granter is in a different org. Explicit IN() filter
      // by user_id avoids the ambiguity entirely.
      const { data: roleRows, error: roleErr } = await supabase
        .from('profile_roles')
        .select('user_id')
        .eq('organization_id', orgId)
        .in('role',            ['cleaner', 'supervisor'])
        .limit(500)
      if (roleErr) throw roleErr
      const userIds = (roleRows ?? []).map((r) => (r as { user_id: string }).user_id)
      if (userIds.length === 0) return []

      const { data: profRows, error: profErr } = await supabase
        .from('profiles')
        .select('id, full_name, email, is_active')
        .in('id',         userIds)
        .eq('is_active',  true)
      if (profErr) throw profErr

      return ((profRows ?? []) as Array<{ id: string; full_name: string; email: string }>)
        .map((p): ReassignCleanerCandidate => ({
          user_id:   p.id,
          full_name: p.full_name,
          email:     p.email,
        }))
        .sort((a, b) => a.full_name.localeCompare(b.full_name))
    },
  })
}
